import React, { useId, useRef, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import Input from 'src/components/form/floating-label/input';
import { ReactComponent as WarningIcon } from 'src/assets/warning-icon.svg';
import { ReactComponent as FeedbackIcon } from 'src/assets/feedback-icon.svg';
import { useOutsideClickTracker } from 'src/hooks/shared-hooks';
import { env } from 'src/constants/env';
import css from './complaint.module.scss';
import API from './api';
import { createPortal } from 'react-dom';
import { ComplaintEntityType } from 'src/constants/complaint-entity-type';
import getValidationSchema from './validate';

Complaint.propTypes = {
  entityId: PropTypes.string.isRequired,
  entityType: PropTypes.oneOf(Object.values(ComplaintEntityType)),
  targetTitle: PropTypes.string.isRequired,
  alignRight: PropTypes.bool,
  triggerVisible: PropTypes.bool,
  className: PropTypes.string,
  wrapper: PropTypes.func
};

export default function Complaint({
  entityId,
  entityType,
  targetTitle,
  alignRight = false,
  triggerVisible,
  className,
  wrapper
}) {
  const complaintTypes = [
    { value: 'Граматична помилка' },
    { value: 'Дані не відповідають дійсності' },
    { value: 'Інше' }
  ];
  const parentRef = useRef(null);
  const [complaintDetails, setComplaintDetails] = useState({ title: '', placeholder: '', showModal: false });
  const [showDescriptionInput, setShowDescriptionInput] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [outOfViewport, setOutOfViewport] = useState(false);
  const descriptionInputId = useId();
  const captchaRef = useRef(null);
  const containerRef = useRef(null);

  const validationSchema = getValidationSchema();

  const methods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      complaintType: '',
      description: ''
    }
  });
  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    reset,
    watch,
    formState: { errors }
  } = methods;
  const watchedDescription = watch('description');
  const [hover, setHover] = useState(false);
  const onMouseEnter = () => setHover(true);
  const onMouseLeave = () => setHover(false);

  const registerAndClearOnChange = function (inputName) {
    return register(inputName, {
      onChange: () => {
        if (errors[inputName]) {
          clearErrors(inputName);
        }
      }
    });
  };

  useOutsideClickTracker(containerRef, () => {
    if (!showPopup || watchedDescription?.length !== 0) return;

    handleClose();
  });

  function handleComplaintShowed(isOpen) {
    setShowPopup(isOpen);
    if (isOpen) {
      const overlay = document.createElement('div');
      overlay.id = 'complaint-overlay';
      document.body.appendChild(overlay);
      document.body.style.overflowY = 'hidden';
      const maxHeight = 300;

      setOutOfViewport(parentRef.current.getBoundingClientRect().top + maxHeight > window.innerHeight);

      const scrollArrow = document.getElementById('scroll-arrow');
      if (scrollArrow) {
        scrollArrow.style.display = 'none';
      }
      return;
    }

    document.body.style.overflowY = 'auto';
    const overlay = document.getElementById('complaint-overlay');
    if (overlay) {
      overlay.remove();
    }

    const scrollArrow = document.getElementById('scroll-arrow');
    if (scrollArrow) {
      scrollArrow.style.display = 'flex';
    }
  }

  function handleClose() {
    handleComplaintShowed(false);
    setShowDescriptionInput(false);
    setComplaintDetails({ title: '', placeholder: '', showModal: false });
    reset();
  }

  function handleComplaintSubmit(data) {
    API.complaint({
      entityId: entityId,
      entityType: entityType,
      description: data.description,
      complaintType: data.complaintType,
      captcha: data.captcha,
      targetTitle
    })
      .then(() => {
        handleClose();
        toast.success('Вашу інформацію успішно надіслано');
      })
      .catch(() => toast.error('Щось пішло не так'));
  }

  function onComplaintTypeClick(title, placeholder) {
    setComplaintDetails((details) => {
      if (details.showModal) {
        return { title: '', placeholder: '', showModal: false };
      }

      return { title, placeholder, showModal: true };
    });
  }

  const handleEvent = useCallback(
    (handleComplaintShowed, setShowDescriptionInput, setComplaintDetails) => {
      handleComplaintShowed(false);
      setShowDescriptionInput(false);
      setComplaintDetails({ title: '', placeholder: '', showModal: false });
      reset();
    },
    [reset]
  );

  useEffect(() => {
    window.addEventListener('popstate', () =>
      handleEvent(handleComplaintShowed, setShowDescriptionInput, setComplaintDetails)
    );
    return () => window.removeEventListener('popstate', handleEvent);
  }, [handleEvent]);

  const complaintNode = (
    <div
      aria-hidden
      className={classNames(
        css.complaint,
        alignRight ? css.alignRight : '',
        showPopup ? css.visible : css.hidden,
        complaintDetails.showModal ? css.fullScreen : '',
        outOfViewport ? css.mirror : ''
      )}
      onClick={(e) => e.stopPropagation()}
      ref={containerRef}>
      <div className={css.container}>
        <button className={css.close} onClick={handleClose}>
          &times;
        </button>
        {!complaintDetails.showModal && (
          <div
            className={css.buttonGroup}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
            onClick={(e) => e.stopPropagation()}>
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                onComplaintTypeClick('Залишити відгук', 'Опис відгука');
                setShowDescriptionInput(true);
                setValue('complaintType', 'Відгук');
              }}>
              <FeedbackIcon />
              <p>Залишити відгук</p>
            </button>
            <button
              type='button'
              onClick={() => onComplaintTypeClick('Поскаржитися', 'Опишіть зафіксовану Вами помилку')}>
              <WarningIcon />
              <p>Поскаржитися</p>
            </button>
          </div>
        )}
        {complaintDetails.showModal && (
          <div className={css.complaintGroup}>
            <h1>{complaintDetails.title}</h1>
            {!showDescriptionInput && (
              <div className={css.group}>
                {complaintTypes.map((complaintType, i) => (
                  <button
                    key={i} /* NOSONAR */
                    type='button'
                    onClick={() => {
                      setValue('complaintType', complaintType.value);
                      setShowDescriptionInput(true);
                    }}>
                    {complaintType.value}
                  </button>
                ))}
              </div>
            )}
            {showDescriptionInput && (
              <FormProvider {...methods}>
                <form
                  onSubmit={handleSubmit(async (data) => {
                    const token = await captchaRef.current?.executeAsync();
                    handleComplaintSubmit({ ...data, captcha: token });
                  })}
                  className={css.form}
                  autoComplete='off'>
                  <div className={css.group}>
                    <Input
                      id={descriptionInputId}
                      label={complaintDetails.placeholder}
                      error={errors?.description?.message}
                      multiline
                      required
                      {...registerAndClearOnChange('description')}
                    />
                    <button type='submit' className={css.submit}>
                      Надіслати
                    </button>
                  </div>
                </form>
              </FormProvider>
            )}
          </div>
        )}
      </div>
    </div>
  );

  function getComplaint() {
    if (!showPopup) return null;

    const rect = parentRef.current.getBoundingClientRect();
    return createPortal(
      <div id='complaint-popup'>
        <div
          style={{
            width: `${rect.width}px`,
            top: `${window.scrollY + rect.top}px`,
            left: `${rect.left}px`
          }}>
          {complaintNode}
        </div>
        {showDescriptionInput && (
          <ReCAPTCHA
            ref={captchaRef}
            className={css.captcha}
            sitekey={env.REACT_APP_NZ_RECAPTCHA_SITE_KEY}
            hl='uk'
            size='invisible'
          />
        )}
      </div>,
      document.body
    );
  }

  const complaint = (
    <div className={classNames(css.complaintContainer, hover ? css.hover : '', className ?? '')} ref={parentRef}>
      <div
        aria-hidden
        className={classNames(css.trigger, showPopup ? css.active : '', triggerVisible ? css.visible : '')}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleComplaintShowed(true);
        }}>
        <span className={css.dot} />
        <span className={css.dot} />
        <span className={css.dot} />
      </div>
      {getComplaint()}
    </div>
  );

  return wrapper(complaint, onMouseEnter, onMouseLeave);
}
